
import React from 'react';

export default function About() {
  return (
    <section
      id="about"
      className="py-16 sm:py-20 px-4 sm:px-6 md:px-16 bg-gradient-to-br from-sky-50 via-blue-50 to-cyan-50 text-gray-900 relative overflow-hidden"
    >
      {/* Animated Background */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-sky-300/40 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-300/30 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
      <div className="absolute top-1/2 right-1/4 w-64 h-64 bg-blue-200/25 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-sky-500 via-blue-600 to-cyan-500 drop-shadow-lg mb-4 animate-pulse">
            About Travelrmania
          </h2>
          <div className="w-20 sm:w-24 h-1 bg-gradient-to-r from-sky-500 to-cyan-500 mx-auto rounded-full"></div>
          <p className="mt-4 text-gray-600 text-lg max-w-2xl mx-auto">
            Your trusted travel partner for unforgettable journeys across India
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
          {/* Left: Text Content */}
          <div className="space-y-4 sm:space-y-6">
            <p className="text-base sm:text-lg leading-relaxed text-gray-700">
              Welcome to <span className="font-bold text-sky-600">Travelrmania</span>, one of 
              the most reputable travel agencies in New Delhi, India. We provide the greatest hotel 
              accommodations, tailored vacation packages, luxury transportation, and expert tourist 
              services to destinations across India and beyond.
            </p>
            
            <p className="text-base sm:text-lg leading-relaxed text-gray-700">
              Our specialty lies in <span className="font-semibold text-cyan-600">luxury tempo traveller 
              rentals, Golden Triangle tours, Himachal adventures</span>, and customized travel experiences. 
              With fully sanitized vehicles, experienced drivers, and 24/7 support, we ensure your 
              journey is safe, comfortable, and unforgettable.
            </p>
            
            <p className="text-base sm:text-lg leading-relaxed text-gray-700">
              From the majestic mountains of Ladakh to the royal palaces of Rajasthan, from spiritual 
              journeys in Uttarakhand to romantic honeymoons in Kashmir — we craft each trip with 
              extreme care and passion. Your dream vacation is our mission!
            </p>

            <div className="flex flex-wrap gap-3 sm:gap-4 pt-4">
              {['Best Price Guarantee', 'Sanitized Vehicles', '24/7 Support', 'Expert Drivers'].map((trait, i) => (
                <span
                  key={i}
                  className="px-3 sm:px-4 py-2 bg-gradient-to-r from-sky-100 to-cyan-100 text-sky-700 rounded-full text-sm font-medium hover:scale-110 transition-transform duration-200 cursor-default shadow-md border border-sky-300"
                >
                  {trait}
                </span>
              ))}
            </div>
          </div>

          {/* Right: Stats/Achievements */}
          <div className="grid grid-cols-2 gap-4 sm:gap-6">
            {[
              { number: '2', label: 'Countries Covered', color: 'from-sky-500 to-blue-600', icon: '🌍' },
              { number: '100+', label: 'Cities Explored', color: 'from-cyan-500 to-blue-600', icon: '🏙️' },
              { number: '10%', label: 'First Trip Discount', color: 'from-sky-400 to-cyan-500', icon: '🎁' },
              { number: '24/7', label: 'Customer Support', color: 'from-blue-500 to-cyan-500', icon: '📞' }
            ].map((stat, i) => (
              <div
                key={i}
                className="bg-white/70 backdrop-blur-sm p-4 sm:p-6 rounded-2xl text-center shadow-lg border border-sky-200 hover:shadow-2xl hover:shadow-sky-300/50 hover:-translate-y-2 transition-all duration-300 cursor-default"
              >
                <div className="text-3xl mb-2">{stat.icon}</div>
                <h3 className={`text-2xl sm:text-3xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent mb-2`}>
                  {stat.number}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Features Section */}
        <div className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              icon: '✅',
              title: 'Passionate Service',
              description: 'We treat every journey as our own, ensuring unforgettable experiences',
              gradient: 'from-sky-500 to-blue-600'
            },
            {
              icon: '🛡️',
              title: 'Safety First',
              description: 'Fully sanitized cars, experienced drivers, and complete safety measures',
              gradient: 'from-cyan-500 to-blue-600'
            },
            {
              icon: '💎',
              title: 'Luxury Fleet',
              description: 'From 4-seater sedans to 26-seater tempo travellers - all premium quality',
              gradient: 'from-sky-400 to-cyan-500'
            }
          ].map((feature, i) => (
            <div
              key={i}
              className="bg-white/70 backdrop-blur-sm p-6 rounded-xl shadow-lg border border-sky-200 hover:shadow-2xl hover:shadow-sky-300/50 hover:scale-105 transition-all duration-300 text-center group"
            >
              <div className="text-4xl mb-3 transform group-hover:scale-110 transition-transform duration-300">{feature.icon}</div>
              <h4 className={`text-lg font-bold bg-gradient-to-r ${feature.gradient} bg-clip-text text-transparent mb-2`}>{feature.title}</h4>
              <p className="text-sm text-gray-600">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-12 sm:mt-16 text-center">
          <div className="inline-block bg-gradient-to-r from-sky-400 via-blue-500 to-cyan-400 p-1 rounded-2xl shadow-2xl shadow-sky-400/30">
            <div className="bg-white px-8 py-6 rounded-2xl">
              <p className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                Ready to start your dream journey?
              </p>
              <p className="text-gray-600 mb-4">
                Contact us at <a href="tel:+918929550611" className="text-sky-600 font-semibold hover:text-sky-700 transition-colors">+91-89295-50611</a>
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="https://wa.me/918929550611"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold rounded-full shadow-lg hover:shadow-cyan-400/50 hover:scale-105 transition-all duration-200"
                >
                  📱 WhatsApp Us
                </a>
                <a
                  href="#packages"
                  className="px-6 py-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-bold rounded-full shadow-lg hover:shadow-sky-400/50 hover:scale-105 transition-all duration-200"
                >
                  🎒 View Packages
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// import React from 'react';

// export default function About() {
//   return (
//     <section
//       id="about"
//       className="py-16 sm:py-20 px-4 sm:px-6 md:px-16 bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900 text-white relative overflow-hidden"
//     >
//       {/* Animated Background */}
//       <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
//       <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>

//       <div className="max-w-6xl mx-auto relative z-10">
//         {/* Section Header */}
//         <div className="text-center mb-12 sm:mb-16">
//           <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-blue-300 to-orange-400 drop-shadow mb-4">
//             About Travelrmania
//           </h2>
//           <div className="w-20 sm:w-24 h-1 bg-gradient-to-r from-blue-500 to-orange-500 mx-auto rounded-full"></div>
//           <p className="mt-4 text-gray-400 text-lg max-w-2xl mx-auto">
//             Your trusted travel partner for unforgettable journeys across India
//           </p>
//         </div>

//         <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
//           {/* Left: Text Content */}
//           <div className="space-y-4 sm:space-y-6">
//             <p className="text-base sm:text-lg leading-relaxed text-gray-300">
//               Welcome to <span className="font-bold text-blue-400">Travelrmania</span>, one of 
//               the most reputable travel agencies in New Delhi, India. We provide the greatest hotel 
//               accommodations, tailored vacation packages, luxury transportation, and expert tourist 
//               services to destinations across India and beyond.
//             </p>
            
//             <p className="text-base sm:text-lg leading-relaxed text-gray-300">
//               Our specialty lies in <span className="font-semibold text-orange-400">luxury tempo traveller 
//               rentals, Golden Triangle tours, Himachal adventures</span>, and customized travel experiences. 
//               With fully sanitized vehicles, experienced drivers, and 24/7 support, we ensure your 
//               journey is safe, comfortable, and unforgettable.
//             </p>
            
//             <p className="text-base sm:text-lg leading-relaxed text-gray-300">
//               From the majestic mountains of Ladakh to the royal palaces of Rajasthan, from spiritual 
//               journeys in Uttarakhand to romantic honeymoons in Kashmir — we craft each trip with 
//               extreme care and passion. Your dream vacation is our mission!
//             </p>

//             <div className="flex flex-wrap gap-3 sm:gap-4 pt-4">
//               {['Best Price Guarantee', 'Sanitized Vehicles', '24/7 Support', 'Expert Drivers'].map((trait, i) => (
//                 <span
//                   key={i}
//                   className="px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500/20 to-orange-500/20 text-blue-300 rounded-full text-sm font-medium hover:scale-110 transition-transform duration-200 cursor-default shadow-lg border border-blue-400/30"
//                 >
//                   {trait}
//                 </span>
//               ))}
//             </div>
//           </div>

//           {/* Right: Stats/Achievements */}
//           <div className="grid grid-cols-2 gap-4 sm:gap-6">
//             {[
//               { number: '102', label: 'Countries Covered', color: 'from-blue-400 to-blue-600', icon: '🌍' },
//               { number: '1752', label: 'Cities Explored', color: 'from-orange-400 to-orange-600', icon: '🏙️' },
//               { number: '10%', label: 'First Trip Discount', color: 'from-blue-300 to-orange-300', icon: '🎁' },
//               { number: '24/7', label: 'Customer Support', color: 'from-orange-400 to-blue-400', icon: '📞' }
//             ].map((stat, i) => (
//               <div
//                 key={i}
//                 className="bg-gray-800/50 backdrop-blur-sm p-4 sm:p-6 rounded-2xl text-center shadow-lg border border-blue-500/20 hover:shadow-2xl hover:shadow-blue-500/20 hover:-translate-y-2 transition-all duration-300 cursor-default"
//               >
//                 <div className="text-3xl mb-2">{stat.icon}</div>
//                 <h3 className={`text-2xl sm:text-3xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent mb-2`}>
//                   {stat.number}
//                 </h3>
//                 <p className="text-sm sm:text-base text-gray-400 font-medium">{stat.label}</p>
//               </div>
//             ))}
//           </div>
//         </div>

//         {/* Additional Features Section */}
//         <div className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
//           {[
//             {
//               icon: '✅',
//               title: 'Passionate Service',
//               description: 'We treat every journey as our own, ensuring unforgettable experiences',
//               gradient: 'from-blue-500 to-blue-600'
//             },
//             {
//               icon: '🛡️',
//               title: 'Safety First',
//               description: 'Fully sanitized cars, experienced drivers, and complete safety measures',
//               gradient: 'from-orange-500 to-orange-600'
//             },
//             {
//               icon: '💎',
//               title: 'Luxury Fleet',
//               description: 'From 4-seater sedans to 26-seater tempo travellers - all premium quality',
//               gradient: 'from-blue-400 to-orange-400'
//             }
//           ].map((feature, i) => (
//             <div
//               key={i}
//               className="bg-gray-800/50 backdrop-blur-sm p-6 rounded-xl shadow-lg border border-blue-500/20 hover:shadow-2xl hover:shadow-blue-500/20 hover:scale-105 transition-all duration-300 text-center group"
//             >
//               <div className="text-4xl mb-3 transform group-hover:scale-110 transition-transform duration-300">{feature.icon}</div>
//               <h4 className={`text-lg font-bold bg-gradient-to-r ${feature.gradient} bg-clip-text text-transparent mb-2`}>{feature.title}</h4>
//               <p className="text-sm text-gray-400">{feature.description}</p>
//             </div>
//           ))}
//         </div>

//         {/* Call to Action */}
//         <div className="mt-12 sm:mt-16 text-center">
//           <div className="inline-block bg-gradient-to-r from-blue-500 via-blue-400 to-orange-500 p-1 rounded-2xl shadow-2xl shadow-blue-500/30">
//             <div className="bg-gray-900 px-8 py-6 rounded-2xl">
//               <p className="text-xl sm:text-2xl font-bold text-white mb-2">
//                 Ready to start your dream journey?
//               </p>
//               <p className="text-gray-400 mb-4">
//                 Contact us at <a href="tel:+918929550611" className="text-blue-400 font-semibold hover:text-blue-300 transition-colors">+91-89295-50611</a>
//               </p>
//               <div className="flex flex-col sm:flex-row gap-3 justify-center">
//                 <a
//                   href="https://wa.me/918929550611"
//                   target="_blank"
//                   rel="noopener noreferrer"
//                   className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold rounded-full shadow-lg hover:shadow-orange-500/50 hover:scale-105 transition-all duration-200"
//                 >
//                   📱 WhatsApp Us
//                 </a>
//                 <a
//                   href="#packages"
//                   className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold rounded-full shadow-lg hover:shadow-blue-500/50 hover:scale-105 transition-all duration-200"
//                 >
//                   🎒 View Packages
//                 </a>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </section>
//   );
// }


// import React from 'react';

// export default function About() {
//   return (
//     <section
//       id="about"
//       className="py-16 sm:py-20 px-4 sm:px-6 md:px-16 bg-gradient-to-br from-white via-blue-50 to-purple-50 text-gray-800"
//     >
//       <div className="max-w-6xl mx-auto">
//         {/* Section Header */}
//         <div className="text-center mb-12 sm:mb-16">
//           <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-purple-600 to-pink-500 drop-shadow mb-4">
//             About Dream to Travels
//           </h2>
//           <div className="w-20 sm:w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
//         </div>

//         <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
//           {/* Left: Text Content */}
//           <div className="space-y-4 sm:space-y-6">
//             <p className="text-base sm:text-lg leading-relaxed text-gray-700">
//               Welcome to <span className="font-bold text-purple-600">Dream to Travels</span>, one of 
//               the most reputable travel agencies in Delhi, India. We provide the greatest hotel 
//               accommodations, tailored vacation packages, luxury transportation, and expert tourist 
//               services to destinations across India and beyond.
//             </p>
            
//             <p className="text-base sm:text-lg leading-relaxed text-gray-700">
//               Our specialty lies in <span className="font-semibold text-blue-600">luxury tempo traveller 
//               rentals, Golden Triangle tours, Himachal adventures</span>, and customized travel experiences. 
//               With fully sanitized vehicles, vaccinated drivers, and 24/7 support, we ensure your 
//               journey is safe, comfortable, and unforgettable.
//             </p>
            
//             <p className="text-base sm:text-lg leading-relaxed text-gray-700">
//               From the majestic mountains of Ladakh to the royal palaces of Rajasthan, from spiritual 
//               journeys in Uttarakhand to romantic honeymoons in Kashmir — we craft each trip with 
//               extreme care and passion. Your dream vacation is our mission!
//             </p>

//             <div className="flex flex-wrap gap-3 sm:gap-4 pt-4">
//               {['Best Price Guarantee', 'Sanitized Vehicles', '24/7 Support', 'Expert Drivers'].map((trait, i) => (
//                 <span
//                   key={i}
//                   className="px-3 sm:px-4 py-2 bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 rounded-full text-sm font-medium hover:scale-110 transition-transform duration-200 cursor-default shadow-sm"
//                 >
//                   {trait}
//                 </span>
//               ))}
//             </div>
//           </div>

//           {/* Right: Stats/Achievements */}
//           <div className="grid grid-cols-2 gap-4 sm:gap-6">
//             {[
//               { number: '102', label: 'Countries Covered', color: 'from-blue-500 to-purple-500', icon: '🌍' },
//               { number: '1752', label: 'Cities Explored', color: 'from-purple-500 to-pink-500', icon: '🏙️' },
//               { number: '15%', label: 'First Trip Discount', color: 'from-pink-500 to-red-500', icon: '🎁' },
//               { number: '24/7', label: 'Customer Support', color: 'from-green-500 to-blue-500', icon: '📞' }
//             ].map((stat, i) => (
//               <div
//                 key={i}
//                 className="bg-white p-4 sm:p-6 rounded-2xl text-center shadow-lg border border-gray-100 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-default"
//               >
//                 <div className="text-3xl mb-2">{stat.icon}</div>
//                 <h3 className={`text-2xl sm:text-3xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent mb-2`}>
//                   {stat.number}
//                 </h3>
//                 <p className="text-sm sm:text-base text-gray-600 font-medium">{stat.label}</p>
//               </div>
//             ))}
//           </div>
//         </div>

//         {/* Additional Features Section */}
//         <div className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
//           {[
//             {
//               icon: '✅',
//               title: 'Passionate Service',
//               description: 'We treat every journey as our own, ensuring unforgettable experiences'
//             },
//             {
//               icon: '🛡️',
//               title: 'Safety First',
//               description: 'Fully sanitized cars, vaccinated drivers, and complete safety measures'
//             },
//             {
//               icon: '💎',
//               title: 'Luxury Fleet',
//               description: 'From 4-seater sedans to 26-seater tempo travellers - all premium quality'
//             }
//           ].map((feature, i) => (
//             <div
//               key={i}
//               className="bg-white p-6 rounded-xl shadow-md border border-gray-100 hover:shadow-xl hover:scale-105 transition-all duration-300 text-center"
//             >
//               <div className="text-4xl mb-3">{feature.icon}</div>
//               <h4 className="text-lg font-bold text-gray-800 mb-2">{feature.title}</h4>
//               <p className="text-sm text-gray-600">{feature.description}</p>
//             </div>
//           ))}
//         </div>

//         {/* Call to Action */}
//         <div className="mt-12 sm:mt-16 text-center">
//           <div className="inline-block bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 p-1 rounded-2xl shadow-xl">
//             <div className="bg-white px-8 py-6 rounded-2xl">
//               <p className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
//                 Ready to start your dream journey?
//               </p>
//               <p className="text-gray-600 mb-4">
//                 Contact us at <a href="tel:+917027005176" className="text-purple-600 font-semibold hover:underline">+91-7027005176</a> or 
//                 <a href="tel:+917082127844" className="text-purple-600 font-semibold hover:underline"> +91-7082127844</a>
//               </p>
//               <div className="flex flex-col sm:flex-row gap-3 justify-center">
//                 <a
//                   href="https://wa.me/917027005176"
//                   target="_blank"
//                   rel="noopener noreferrer"
//                   className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-full shadow-lg hover:scale-105 transition-transform duration-200"
//                 >
//                   📱 WhatsApp Us
//                 </a>
//                 <a
//                   href="#packages"
//                   className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold rounded-full shadow-lg hover:scale-105 transition-transform duration-200"
//                 >
//                   🎒 View Packages
//                 </a>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </section>
//   );
// }

// import React from 'react';
// import { motion } from 'framer-motion';

// export default function About() {
//   return (
//     <section
//       id="about"
//       className="py-16 sm:py-20 px-4 sm:px-6 md:px-16 bg-gradient-to-br from-white via-blue-50 to-purple-50 text-gray-800"
//     >
//       <div className="max-w-6xl mx-auto">
//         {/* Section Header */}
//         <motion.div
//           initial={{ opacity: 0, y: 30 }}
//           whileInView={{ opacity: 1, y: 0 }}
//           transition={{ duration: 0.6 }}
//           viewport={{ once: true }}
//           className="text-center mb-12 sm:mb-16"
//         >
//           <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-purple-600 to-pink-500 drop-shadow mb-4">
//             About Me
//           </h2>
//           <motion.div
//             initial={{ scaleX: 0 }}
//             whileInView={{ scaleX: 1 }}
//             transition={{ delay: 0.3, duration: 0.8 }}
//             className="w-20 sm:w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"
//           />
//         </motion.div>

//         <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
//           {/* Left: Text Content */}
//           <motion.div
//             initial={{ opacity: 0, x: -50, rotateY: -15 }}
//             whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
//             transition={{ duration: 0.8, type: "spring", stiffness: 80 }}
//             viewport={{ once: true }}
//             className="space-y-4 sm:space-y-6"
//             style={{ transformStyle: 'preserve-3d' }}
//           >
//             <motion.p 
//               className="text-base sm:text-lg leading-relaxed text-gray-700"
//               whileHover={{ x: 5, scale: 1.02 }}
//               transition={{ type: "spring", stiffness: 300 }}
//             >
//               I'm a passionate <span className="font-bold text-purple-600">Full Stack Developer</span> with 
//               2.8+ years of experience building scalable web and mobile applications. I specialize in 
//               creating seamless user experiences with modern technologies.
//             </motion.p>
            
//             <motion.p 
//               className="text-base sm:text-lg leading-relaxed text-gray-700"
//               whileHover={{ x: 5, scale: 1.02 }}
//               transition={{ type: "spring", stiffness: 300 }}
//             >
//               My expertise spans across <span className="font-semibold text-blue-600">React.js, Node.js, 
//               React Native</span>, and cloud technologies. I love solving complex problems and turning 
//               ideas into reality through clean, efficient code.
//             </motion.p>
            
//             <motion.p 
//               className="text-base sm:text-lg leading-relaxed text-gray-700"
//               whileHover={{ x: 5, scale: 1.02 }}
//               transition={{ type: "spring", stiffness: 300 }}
//             >
//               When I'm not coding, you'll find me exploring new technologies, contributing to open-source 
//               projects, or sharing knowledge through my YouTube channel.
//             </motion.p>

//             <motion.div
//               initial={{ opacity: 0, y: 20 }}
//               whileInView={{ opacity: 1, y: 0 }}
//               transition={{ delay: 0.5, duration: 0.6 }}
//               className="flex flex-wrap gap-3 sm:gap-4 pt-4"
//             >
//               {['Problem Solver', 'Team Player', 'Quick Learner', 'Innovation Focused'].map((trait, i) => (
//                 <motion.span
//                   key={i}
//                   className="px-3 sm:px-4 py-2 bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 rounded-full text-sm font-medium"
//                   whileHover={{ 
//                     scale: 1.1, 
//                     rotateZ: 5,
//                     boxShadow: "0 5px 15px rgba(147,51,234,0.3)"
//                   }}
//                   transition={{ type: "spring", stiffness: 300 }}
//                 >
//                   {trait}
//                 </motion.span>
//               ))}
//             </motion.div>
//           </motion.div>

//           {/* Right: Stats/Achievements */}
//           <motion.div
//             initial={{ opacity: 0, x: 50, rotateY: 15 }}
//             whileInView={{ opacity: 1, x: 0, rotateY: 0 }}
//             transition={{ duration: 0.8, type: "spring", stiffness: 80 }}
//             viewport={{ once: true }}
//             className="grid grid-cols-2 gap-4 sm:gap-6"
//             style={{ transformStyle: 'preserve-3d' }}
//           >
//             {[
//               { number: '2.8+', label: 'Years Experience', color: 'from-blue-500 to-purple-500' },
//               { number: '15+', label: 'Projects Completed', color: 'from-purple-500 to-pink-500' },
//               { number: '5+', label: 'Technologies Mastered', color: 'from-pink-500 to-red-500' },
//               { number: '100%', label: 'Client Satisfaction', color: 'from-green-500 to-blue-500' }
//             ].map((stat, i) => (
//               <motion.div
//                 key={i}
//                 initial={{ opacity: 0, y: 30, rotateX: -20 }}
//                 whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
//                 transition={{ delay: i * 0.1, duration: 0.6 }}
//                 whileHover={{ 
//                   y: -10, 
//                   rotateX: 10,
//                   scale: 1.05,
//                   boxShadow: "0 20px 40px rgba(0,0,0,0.1)"
//                 }}
//                 className="bg-white p-4 sm:p-6 rounded-2xl text-center shadow-lg border border-gray-100 transform transition-all duration-300"
//                 style={{ transformStyle: 'preserve-3d' }}
//               >
//                 <motion.h3 
//                   className={`text-2xl sm:text-3xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent mb-2`}
//                   whileHover={{ scale: 1.1 }}
//                 >
//                   {stat.number}
//                 </motion.h3>
//                 <p className="text-sm sm:text-base text-gray-600 font-medium">{stat.label}</p>
//               </motion.div>
//             ))}
//           </motion.div>
//         </div>
//       </div>
//     </section>
//   );
// }

// export default function About() {
//   return (
//     <section
//       id="about"
//       className="py-20 px-6 md:px-16 bg-gradient-to-b from-gray-50 via-white to-gray-100 text-gray-800"
//     >
//       <div className="max-w-5xl mx-auto">
//         {/* Heading */}
//         <h2 className="text-4xl sm:text-5xl font-extrabold mb-8 text-center text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 drop-shadow">
//           About Me
//         </h2>

//         {/* Paragraphs */}
//         <div className="space-y-6 text-lg leading-relaxed text-justify">
//           <p>
//             I'm a passionate Full Stack Developer with over 2.5 years of professional experience building responsive and performant web applications. My primary stack includes <strong>React.js</strong>, <strong>Node.js</strong>, <strong>MongoDB</strong>, and modern tools such as Tailwind CSS, Express, and TypeScript. I love solving real-world problems with clean, scalable code.
//           </p>

//           <p>
//             I’ve worked on a wide range of projects including B2B dashboards, custom CMS systems, and mobile-first e-commerce apps. I focus heavily on writing reusable components, ensuring accessibility, and optimizing performance for fast load times.
//           </p>

//           <p>
//             On the backend, I build RESTful and GraphQL APIs, manage secure authentication, and deploy scalable microservices using AWS services like EC2, S3, Lambda, and DynamoDB. I’ve also integrated third-party services like Stripe, Firebase, Razorpay, and Twilio.
//           </p>

//           <p>
//             Beyond code, I collaborate with designers and project managers, mentor junior developers, and continuously improve my skills by contributing to open-source projects and staying up to date with the latest tech trends.
//           </p>

//           <p>
//             Whether it's crafting seamless UI/UX experiences or building robust server-side logic, I enjoy delivering impactful solutions that help businesses grow.
//           </p>
//         </div>
//       </div>
//     </section>
//   );
// }

// export default function About() {
//   return (
//     <section id="about" className="py-16 px-6 bg-gray-50">
//       <h2 className="text-3xl font-bold mb-4">About Me</h2>
//       <p className="max-w-3xl">I am a full-stack developer with 2.5+ years of experience in building scalable apps. I specialize in React.js, Node.js, and cloud services like AWS.</p>
//     </section>
//   );
// }
